/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react';
import _ from 'lodash';
import { v4 } from 'uuid';
import { compose } from 'redux';
import { PropTypes } from 'prop-types';
import arrayMove from 'array-move';

import {
  withFormik,
  Field,
  Form as FormikForm,
} from 'formik';

import {
  Form,
} from 'semantic-ui-react';

import { globalTheme, InputFormField, TextAreaFormField } from '@playbookux/playbook-web-core';

import {
  Container,
  ContextSegment,
  FormContainer,
  HeaderSegmentContainer,
  Optional,
  SegmentTitle,
  Text,
  ErrorMessage,
  ContainerDefault,
  ButtonContainer,
  CancelButton,
  SaveButton,
  Title,
  ContainerLoading,
  Loader,
} from './styles';

import schema from './schema-template';
import QuestionsForm from '../questions-form';

import cloneQuestionsWithOptions from '../../../core/utils/clone-questions-with-options';

const TemplateQuestions = (props) => {
  const {
    errors,
    setFieldValue,
    setValues,
    touched,
    values,
    onPreviewStudy,
    title,
    onCancel,
    templateValues,
    loading,
    isEdit,
  } = props;

  useEffect(() => {
    if (templateValues && !_.isEmpty(templateValues)) {
      setValues({ ...templateValues });
    }
  }, [setValues, templateValues]);

  const handleChange = (event, obj, newIndex) => {
    const { checked, value } = obj;

    const valuesClone = _.cloneDeep(values);

    valuesClone.tasks[newIndex][value] = checked;

    if (!checked && value === 'addUrl') valuesClone.tasks[newIndex].url = '';

    // Update Formik values
    setValues({
      ...valuesClone,
    });
  };

  const handleSortEndOptions = ({ oldIndex, newIndex }) => {
    const valuesClone = _.cloneDeep(values);

    if (valuesClone.tasks[oldIndex]) {
      valuesClone.tasks = arrayMove(valuesClone.tasks, oldIndex, newIndex);

      setValues({
        ...valuesClone,
      });
    }
  };

  const handleNewQuestion = (key) => {
    const question = {
      id: v4(),
      label: '',
      questionType: key,
      ...(key === 'range' && {
        range: 5,
        leftLabel: '',
        rightLabel: '',
      }),
      ...((key === 'single_select' || key === 'multiple_select') && {
        questionOptions: [
          { id: v4(), label: '', optionType: 'answer' },
          { id: v4(), label: '', optionType: 'answer' },
        ],
      }),
    };

    if (isEdit) {
      question.isNew = true;
    }

    values.tasks.push(question);
    setValues({ ...values });
  };

  const handleRemoveQuestion = (newIndex) => {
    const valuesClone = _.cloneDeep(values);

    valuesClone.tasks = _.pull(valuesClone.tasks, valuesClone.tasks[newIndex]);
    setValues({ ...valuesClone });
  };

  const handleDuplicateQuestion = (index) => {
    const valuesClone = _.cloneDeep(values);

    const duplicatedQuestion = _.cloneDeep(valuesClone.tasks[index]);
    duplicatedQuestion.id = v4();
    if (isEdit) {
      duplicatedQuestion.isNew = true;
    }

    if (!_.isEmpty(duplicatedQuestion.questionOptions)) {
      _.forEach(duplicatedQuestion.questionOptions, (opt, i) => {
        duplicatedQuestion.questionOptions[i].id = v4();
      });
    }
    valuesClone.tasks.splice(index + 1, 0, duplicatedQuestion);

    setValues({ ...valuesClone });
  };

  return (
    <>
      <Container>
        <FormContainer>
          <FormikForm>
            <Form as="div">

              {title && (
                <Title>
                  {title}
                </Title>
              )}

              {loading && (
                <ContainerLoading>
                  <Loader data-loader="template-questions-loader" />
                </ContainerLoading>
              )}

              {!loading && (
                <>
                  <ContainerDefault>
                    <Field
                      labelFontSize="14px"
                      labelColor={globalTheme.color.textLabel}
                      labelText="Template Name"
                      name="templateName"
                      placeholder="Enter name"
                      component={InputFormField}
                    />

                    <Field
                      labelFontSize="14px"
                      labelColor={globalTheme.color.textLabel}
                      labelText="Template Description"
                      name="templateDescription"
                      placeholder="Enter description"
                      component={InputFormField}
                    />

                  </ContainerDefault>

                  <ContextSegment>
                    <HeaderSegmentContainer>
                      <SegmentTitle>Context</SegmentTitle>
                      <Optional>- optional</Optional>
                    </HeaderSegmentContainer>

                    <Text>
                      Create the context of your study so testers can put themselves
                      in the correct mindset
                    </Text>

                    <Field
                      name="context"
                      placeholder="Imagine you're looking for a new software for your role as a..."
                      component={TextAreaFormField}
                      height="84px"
                    />
                  </ContextSegment>

                  {errors && errors.tasks && _.isString(errors.tasks) && (
                    <ErrorMessage>{errors.tasks}</ErrorMessage>
                  )}

                  <QuestionsForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleSortOnEnd={handleSortEndOptions}
                    handleNewQuestion={(key) => handleNewQuestion(key)}
                    handleRemoveQuestion={handleRemoveQuestion}
                    onClickDuplicate={handleDuplicateQuestion}
                    setFieldValue={setFieldValue}
                    handlePreviewStudy={() => onPreviewStudy(values)}
                  />

                  <ButtonContainer>
                    <CancelButton
                      onClick={onCancel}
                      loading={false}
                      type="button"
                    >
                      Cancel
                    </CancelButton>

                    <SaveButton
                      type="submit"
                      loading={false}
                      disabled={values.tasks && values.tasks?.length === 0}
                    >
                      Save
                    </SaveButton>
                  </ButtonContainer>
                </>
              )}

            </Form>
          </FormikForm>
        </FormContainer>
      </Container>

    </>
  );
};

TemplateQuestions.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  errors: PropTypes.shape({
    tasks: PropTypes.string,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  touched: PropTypes.shape({}).isRequired,
  values: PropTypes.shape({
    tasks: PropTypes.shape({
      push: PropTypes.func,
      length: PropTypes.number,
    }),
  }).isRequired,
  title: PropTypes.string,
  errorTemplate: PropTypes.shape({}),
  onCancel: PropTypes.func.isRequired,
  templateValues: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  onPreviewStudy: PropTypes.func,
  isEdit: PropTypes.bool,
};

TemplateQuestions.defaultProps = {
  title: null,
  errorTemplate: null,
  templateValues: null,
  loading: false,
  onPreviewStudy: null,
  isEdit: false,
};

export default compose(
  withFormik({
    enableReinitialize: false,
    validationSchema: schema,
    mapPropsToValues: ({ formValues = {}, templateName }) => ({
      tasks: _.isEmpty(formValues.tasks) ? [] : formValues.tasks,
      templateName,
    }),

    handleSubmit: (values, { props }) => {
      const {
        onSave,
        templateValues,
      } = props;

      const previousTasks = _.get(props.templateValues, 'tasks', []);

      const {
        templateName,
        templateDescription,
        context,
        tasks,
        surveyId,
      } = values;

      const newTasks = cloneQuestionsWithOptions(tasks, templateValues);

      const deletedTasks = [];

      // Add the deleted tasks to the array to know what task should delete.
      if (!_.isEmpty(previousTasks)) {
        previousTasks.forEach((previousTask) => {
          if (_.isEmpty(_.filter(values.tasks, (task) => task.id === previousTask.id))) {
            deletedTasks.push({
              id: previousTask.id,
              _destroy: true,
            });
          }
        });
      }

      const tasksSave = _.concat(newTasks, deletedTasks);

      // Sanitize tasks, removing imageUrl and videoUrl
      tasksSave.forEach((task) => {
        delete task.imageUrl;
        delete task.videoUrl;
      });

      const surveyAttributes = {
        surveyType: 'session',
        questionsAttributes: tasksSave,
      };

      if (surveyId) {
        surveyAttributes.id = surveyId;
      }

      const newValues = {
        name: templateName,
        description: templateDescription,
        templateContext: context,
        surveyAttributes,
      };

      onSave(newValues);
    },
  }),
)(TemplateQuestions);
